import React, { useEffect, useState } from 'react';
import { API_URL } from './constants';
import { formatPhoneInput } from './utils';
import FormRenderer, { matchFormType } from '../forms/FormRenderer';

function AccountPage({ guestInfo, accountData, accountLoading, accountTab, onTabChange, onSetAccountData, onLoadFullAccountData, onBackToBooking, onRebookClick, onLogout, selectedCenter }) {

  // Profile form state
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Cancel appointment state
  const [cancellingAppt, setCancellingAppt] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');

  // Package & gift card expand state
  const [expandedPkg, setExpandedPkg] = useState(null);
  const [expandedGc, setExpandedGc] = useState(null);

  // Feedback state
  const [feedbackAppt, setFeedbackAppt] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(null); // appt id that was submitted

  // Mobile menu hub state
  const [mobileSection, setMobileSection] = useState(null);

  // Forms state
  const [formsLoading, setFormsLoading] = useState(false);
  const [accountForms, setAccountForms] = useState(null);
  const [activeForm, setActiveForm] = useState(null);

  const menuItems = [
    { key: 'upcoming', label: 'Upcoming Appointments' },
    { key: 'history', label: 'Appointment History' },
    { key: 'forms', label: 'Forms' },
    { key: 'giftCards', label: 'Gift Cards' },
    { key: 'packages', label: 'Packages' },
    { key: 'recommended', label: 'For You' },
    { key: 'profile', label: 'Profile' },
  ];

  function handleMenuTap(key) {
    setMobileSection(key);
    onTabChange(key);
  }

  function handleMobileBack() {
    setMobileSection(null);
  }

  // Load full account summary on mount if we only have lightweight data
  useEffect(() => {
    if (guestInfo?.guestId && !accountData?.upcoming && onLoadFullAccountData) {
      onLoadFullAccountData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestInfo?.guestId]);

  // Pre-fetch history on mount so it's ready when user clicks the tab
  useEffect(() => {
    if (guestInfo?.guestId && !accountData?.history) {
      fetch(`${API_URL}/api/account/appointment-history/${guestInfo.guestId}`)
        .then(r => r.json())
        .then(data => onSetAccountData(prev => ({ ...prev, history: data.appointments })))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestInfo?.guestId]);

  // Fetch gift cards fresh when tab is opened
  useEffect(() => {
    if (accountTab === 'giftCards' && guestInfo?.guestId) {
      fetch(`${API_URL}/api/account/giftcards/${guestInfo.guestId}`)
        .then(r => r.json())
        .then(data => onSetAccountData(prev => ({ ...prev, giftCards: data.giftCards || [] })))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTab, guestInfo?.guestId]);

  // Fetch packages fresh when tab is opened
  useEffect(() => {
    if (accountTab === 'packages' && guestInfo?.guestId) {
      fetch(`${API_URL}/api/account/packages/${guestInfo.guestId}`)
        .then(r => r.json())
        .then(data => onSetAccountData(prev => ({ ...prev, packages: data.packages || [] })))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTab, guestInfo?.guestId]);

  // Fetch unfilled forms for upcoming appointments when Forms tab is opened
  useEffect(() => {
    if (accountTab === 'forms' && guestInfo?.guestId && accountForms === null) {
      setFormsLoading(true);
      fetch(`${API_URL}/api/account/forms/${guestInfo.guestId}`)
        .then(r => r.json())
        .then(data => setAccountForms(data.forms || []))
        .catch(() => setAccountForms([]))
        .finally(() => setFormsLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTab, guestInfo?.guestId]);

  // Load profile when profile tab is selected
  useEffect(() => {
    if (accountTab === 'profile' && guestInfo?.guestId && !profile) {
      setProfileLoading(true);
      fetch(`${API_URL}/api/account/profile/${guestInfo.guestId}`)
        .then(r => r.json())
        .then(data => {
          // Parse DOB into date input format (YYYY-MM-DD)
          let dob = '';
          if (data.dob) {
            const d = new Date(data.dob);
            if (!isNaN(d)) dob = d.toISOString().split('T')[0];
          }
          setProfile({ ...data, dob });
        })
        .catch(() => setProfileError('Failed to load profile'))
        .finally(() => setProfileLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTab, guestInfo?.guestId]);

  function handleProfileUpdate() {
    if (!profile.firstName?.trim() || !profile.lastName?.trim()) {
      setProfileError('First name and last name are required.');
      return;
    }
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');
    fetch(`${API_URL}/api/account/profile/${guestInfo.guestId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profile)
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setProfileSuccess('Profile updated!');
          setTimeout(() => setProfileSuccess(''), 3000);
        } else {
          setProfileError(data.error || 'Update failed');
        }
      })
      .catch(() => setProfileError('Something went wrong'))
      .finally(() => setProfileSaving(false));
  }

  function handleCancelAppointment() {
    if (!cancellingAppt) return;
    setCancelLoading(true);
    setCancelError('');
    fetch(`${API_URL}/api/kiosk/cancel-appointment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceId: cancellingAppt.invoiceId, guestId: guestInfo.guestId })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          onSetAccountData(prev => ({
            ...prev,
            upcoming: (prev.upcoming || []).filter(a => a.invoiceId !== cancellingAppt.invoiceId)
          }));
          setCancellingAppt(null);
        } else {
          setCancelError(data.error || 'Could not cancel. Please call us.');
        }
      })
      .catch(() => setCancelError('Something went wrong. Please try again.'))
      .finally(() => setCancelLoading(false));
  }

  function handleFeedbackSubmit() {
    if (!feedbackAppt || feedbackRating === 0) return;
    setFeedbackSubmitting(true);
    fetch(`${API_URL}/api/kiosk/rating`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        guestId: guestInfo.guestId,
        appointmentGroupId: feedbackAppt.id,
        appointmentId: feedbackAppt.appointmentId,
        rating: feedbackRating,
        comment: feedbackComment,
        therapistId: feedbackAppt.therapist?.id,
        therapistName: feedbackAppt.therapist?.name,
        guestName: guestInfo.firstName,
        guestLastName: guestInfo.lastName,
        centerId: feedbackAppt.center?.id,
      })
    })
      .then(r => r.json())
      .then(() => {
        setFeedbackSuccess(feedbackAppt.id || feedbackAppt.appointmentId);
        setFeedbackAppt(null);
        setFeedbackRating(0);
        setFeedbackComment('');
        // Mark as submitted in local data
        onSetAccountData(prev => ({
          ...prev,
          history: (prev.history || []).map(a =>
            (a.id === feedbackAppt.id || a.appointmentId === feedbackAppt.appointmentId)
              ? { ...a, isFeedbackSubmitted: true }
              : a
          )
        }));
      })
      .catch(() => alert('Could not submit feedback. Please try again.'))
      .finally(() => setFeedbackSubmitting(false));
  }

  // Staff recommendations
  const [staffRecs, setStaffRecs] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);

  // Package purchase state
  const [purchasingPkg, setPurchasingPkg] = useState(null); // { recId, pkg }
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');

  async function handlePackagePurchase() {
    if (!purchasingPkg) return;
    const { recId, pkg } = purchasingPkg;
    setPurchaseLoading(true);
    setPurchaseError('');
    try {
      const centerId = selectedCenter?.id || guestInfo?.centerId;
      // Step 1: Create invoice
      const invoiceRes = await fetch(`${API_URL}/api/packages/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestId: guestInfo.guestId, packageId: pkg.id, centerId })
      });
      const invoiceData = await invoiceRes.json();
      if (!invoiceData.success) throw new Error(invoiceData.error || 'Could not create invoice');

      // Step 2: Auto-apply discount code if staff added one
      if (pkg.discount?.code && invoiceData.invoiceId) {
        try {
          await fetch(`${API_URL}/api/invoice/apply-coupon`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invoiceId: invoiceData.invoiceId, couponCode: pkg.discount?.code, centerId })
          });
        } catch (e) {
          // Discount failed but purchase can still proceed
          console.warn('Discount code failed to apply:', e);
        }
      }

      // Step 3: Mark recommendation as purchased (itemId = the one package
      // bought, so only its value is credited — not the whole rec)
      if (recId) {
        fetch(`${API_URL}/api/staff/recommendations/${recId}/purchased`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ itemId: pkg.id }),
        }).catch(() => {});
        setStaffRecs(prev => (prev || []).filter(r => r.id !== recId));
      }

      // Step 4: Redirect to payment
      if (invoiceData.hostedPaymentUrl) {
        window.location.href = invoiceData.hostedPaymentUrl;
      } else {
        setPurchasingPkg(null);
      }
    } catch (err) {
      setPurchaseError(err.message || 'Something went wrong');
    } finally {
      setPurchaseLoading(false);
    }
  }

  function handleBookRecommendedService(rec, svc) {
    // Use rebook flow — build a minimal appointment-like object
    onRebookClick({
      service: { id: svc.id, name: svc.name },
      center: selectedCenter ? { id: selectedCenter.id, name: selectedCenter.name } : undefined,
    });
  }

  useEffect(() => {
    if (accountTab === 'recommended' && guestInfo?.guestId && !staffRecs) {
      setRecsLoading(true);
      fetch(`${API_URL}/api/staff/recommendations/profile/${guestInfo.guestId}`)
        .then(r => r.json())
        .then(data => setStaffRecs(data.recommendations || []))
        .catch(() => setStaffRecs([]))
        .finally(() => setRecsLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTab, guestInfo?.guestId]);

  const recentVisits = accountData?.recentVisits || [];

  const sectionTitle = menuItems.find(m => m.key === mobileSection)?.label || '';

  return (
    <div className="step-content">
      {/* Desktop title */}
      <h1 className="step-title account-desktop-title">MY ACCOUNT</h1>
      <p className="account-greeting account-desktop-title">Welcome, {guestInfo.firstName}!</p>

      {/* Mobile title + close */}
      <div className="account-mobile-title">
        <h1 className="step-title" style={{ margin: 0 }}>{guestInfo.firstName?.toUpperCase()}'S ACCOUNT</h1>
        <button className="account-close-btn" onClick={onBackToBooking}>&#10005;</button>
      </div>

      {/* Quick Book — horizontal scrolling rebook bar */}
      {!accountLoading && recentVisits.length > 0 && (
        <div className="quickbook-section">
          <div className="quickbook-label">QUICK BOOK</div>
          <div className="quickbook-scroll">
            {recentVisits.map((visit, i) => (
              <div key={i} className="quickbook-card" onClick={() => onRebookClick(visit)}>
                <div className="quickbook-service">{visit.service?.name || 'Service'}</div>
                <div className="quickbook-detail">
                  {visit.date ? new Date(visit.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                </div>
                {visit.therapist?.name && (
                  <div className="quickbook-detail">w/ {visit.therapist.name}</div>
                )}
                <div className="quickbook-btn">REBOOK</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mobile menu hub — shown when no section selected */}
      <div className={`account-menu ${mobileSection ? 'hidden' : ''}`}>
        {menuItems.map(item => (
          <button key={item.key} className="account-menu-row" onClick={() => handleMenuTap(item.key)}>
            <span>{item.label}</span>
            <span className="account-menu-chevron">&#8250;</span>
          </button>
        ))}
        {onLogout && (
          <button className="account-menu-row account-menu-logout" onClick={onLogout}>
            <span>Log Out</span>
            <span className="account-menu-chevron">&#8250;</span>
          </button>
        )}
      </div>

      {/* Mobile section back header — shown when in a section */}
      {mobileSection && (
        <button className="account-mobile-back" onClick={handleMobileBack}>
          &#8592; {sectionTitle}
        </button>
      )}

      {/* Desktop tabs */}
      <div className="account-tabs">
        {['upcoming', 'recommended', 'history', 'forms', 'giftCards', 'packages', 'profile'].map(tab => (
          <button
            key={tab}
            className={`account-tab ${accountTab === tab ? 'active' : ''}`}
            onClick={() => onTabChange(tab)}
          >
            {tab === 'upcoming' ? 'Upcoming' : tab === 'recommended' ? 'For You' : tab === 'history' ? 'History' : tab === 'forms' ? 'Forms' : tab === 'giftCards' ? 'Gift Cards' : tab === 'packages' ? 'Packages' : 'Profile'}
          </button>
        ))}
      </div>

      {accountLoading && <div className="loading-spinner" style={{margin:'40px auto'}}></div>}

      {!accountLoading && accountData && (
        <div className={`account-content ${!mobileSection ? 'mobile-menu-visible' : ''}`}>

          {/* Upcoming Appointments */}
          {accountTab === 'upcoming' && (
            <div className="account-section">
              {accountData.upcoming?.length > 0 ? (
                accountData.upcoming.map((appt, i) => (
                  <div key={i} className="account-card upcoming-card">
                    <div className="account-card-header">
                      <strong>{appt.service?.name || 'Appointment'}</strong>
                      <span className="account-card-price">${appt.price}</span>
                    </div>
                    <div className="account-card-details">
                      <span>{appt.startTime ? new Date(appt.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}</span>
                      <span>{appt.startTime ? new Date(appt.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}</span>
                      {appt.therapist?.name && <span>with {appt.therapist.name}</span>}
                      {appt.center?.name && <span>@ {appt.center.name}</span>}
                    </div>
                    <div className="upcoming-actions">
                      <button className="upcoming-btn upcoming-btn-reschedule" onClick={() => onRebookClick(appt)}>
                        RESCHEDULE
                      </button>
                      <button className="upcoming-btn upcoming-btn-cancel" onClick={() => { setCancelError(''); setCancellingAppt(appt); }}>
                        CANCEL
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="account-empty">
                  <p>No upcoming appointments</p>
                  <button className="auth-button" onClick={onBackToBooking}>BOOK NOW</button>
                </div>
              )}
            </div>
          )}

          {/* Cancel confirmation modal */}
          {cancellingAppt && (
            <div className="auto-advance-overlay" onClick={() => { setCancellingAppt(null); setCancelError(''); }}>
              <div className="auto-advance-modal" onClick={e => e.stopPropagation()}>
                <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Cancel Appointment?</p>
                <p style={{ marginBottom: 4 }}>
                  <strong>{cancellingAppt.service?.name}</strong>
                </p>
                <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
                  {cancellingAppt.startTime ? new Date(cancellingAppt.startTime).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : ''}
                  {' at '}
                  {cancellingAppt.startTime ? new Date(cancellingAppt.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                </p>
                {cancelError && <p style={{ color: '#e53935', fontSize: 13, marginBottom: 12 }}>{cancelError}</p>}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="upcoming-btn upcoming-btn-cancel"
                    style={{ flex: 1, padding: '14px 0' }}
                    onClick={handleCancelAppointment}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? 'CANCELLING...' : 'YES, CANCEL'}
                  </button>
                  <button
                    className="upcoming-btn upcoming-btn-reschedule"
                    style={{ flex: 1, padding: '14px 0' }}
                    onClick={() => { setCancellingAppt(null); setCancelError(''); }}
                  >
                    KEEP IT
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Feedback modal */}
          {feedbackAppt && (
            <div className="auto-advance-overlay" onClick={() => setFeedbackAppt(null)}>
              <div className="auto-advance-modal feedback-modal" onClick={e => e.stopPropagation()}>
                <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>How was your visit?</p>
                <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
                  {feedbackAppt.service?.name || feedbackAppt.services?.[0]?.name}
                  {feedbackAppt.therapist?.name ? ` with ${feedbackAppt.therapist.name}` : ''}
                </p>
                <div className="feedback-stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`feedback-star ${feedbackRating >= star ? 'active' : ''}`}
                      onClick={() => setFeedbackRating(star)}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <textarea
                  className="feedback-comment"
                  placeholder="Any comments? (optional)"
                  value={feedbackComment}
                  onChange={e => setFeedbackComment(e.target.value)}
                  rows={3}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button
                    className="upcoming-btn upcoming-btn-cancel"
                    style={{ flex: 1, padding: '14px 0' }}
                    onClick={() => setFeedbackAppt(null)}
                  >
                    CANCEL
                  </button>
                  <button
                    className="auth-button"
                    style={{ flex: 1, margin: 0, opacity: feedbackRating === 0 ? 0.5 : 1 }}
                    onClick={handleFeedbackSubmit}
                    disabled={feedbackRating === 0 || feedbackSubmitting}
                  >
                    {feedbackSubmitting ? 'SENDING...' : 'SUBMIT'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Staff Recommendations */}
          {accountTab === 'recommended' && (
            <div className="account-section">
              {recsLoading ? (
                <div className="loading-spinner" style={{margin:'40px auto'}}></div>
              ) : staffRecs && staffRecs.length > 0 ? (
                staffRecs.map((rec, i) => (
                  <div key={rec.id || i} className="account-card" style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      {rec.techId && (
                        <img
                          src={`${API_URL}/api/therapist-image/${rec.techId}?size=px100`}
                          alt={rec.techName}
                          style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid #E8A0B4' }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div>
                        <strong style={{ color: '#3a3a3a' }}>{rec.techName} recommends</strong>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {new Date(rec.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                    </div>
                    {(rec.packages || []).map((pkg, j) => {
                      const isService = pkg.type === 'service';
                      return (
                        <div key={j} style={{
                          padding: '12px 14px', background: '#fdf5f8', borderRadius: 10, marginBottom: 6,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: '#3a3a3a' }}>
                                {pkg.name}
                                {pkg.type && <span style={{ fontSize: 10, fontWeight: 500, color: '#999', marginLeft: 8, textTransform: 'uppercase' }}>{pkg.type}</span>}
                              </div>
                              {pkg.description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{pkg.description}</div>}
                              {pkg.discount?.code && (
                                <div style={{ fontSize: 12, color: '#4caf50', fontWeight: 600, marginTop: 4 }}>
                                  {rec.techName} applied a discount for you{pkg.discount.label ? ` — ${pkg.discount.label}` : ''}!
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: '#c4879a' }}>
                                ${pkg.price?.toFixed(2)}
                              </div>
                            </div>
                          </div>
                          <div style={{ marginTop: 10 }}>
                            {isService ? (
                              <button
                                className="auth-button"
                                style={{ margin: 0, padding: '10px 20px', fontSize: 13 }}
                                onClick={() => handleBookRecommendedService(rec, pkg)}
                              >
                                BOOK NOW
                              </button>
                            ) : pkg.type === 'product' ? (
                              <div style={{ fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                                Ask about this at your next visit
                              </div>
                            ) : (
                              <button
                                className="auth-button"
                                style={{ margin: 0, padding: '10px 20px', fontSize: 13 }}
                                onClick={() => { setPurchaseError(''); setPurchasingPkg({ recId: rec.id, pkg }); }}
                              >
                                BUY PACKAGE
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              ) : (
                <div className="account-empty"><p>No recommendations yet</p></div>
              )}
            </div>
          )}

          {/* Package Purchase confirmation modal */}
          {purchasingPkg && (
            <div className="auto-advance-overlay" onClick={() => { setPurchasingPkg(null); setPurchaseError(''); }}>
              <div className="auto-advance-modal" onClick={e => e.stopPropagation()}>
                <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Purchase Package</p>
                <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{purchasingPkg.pkg.name}</p>
                <p style={{ color: '#c4879a', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
                  ${purchasingPkg.pkg.price?.toFixed(2)}
                </p>
                {purchasingPkg.pkg.discount?.code && (
                  <p style={{ color: '#4caf50', fontSize: 13, marginBottom: 12 }}>
                    Discount will be applied at checkout
                  </p>
                )}
                {purchasingPkg.pkg.description && (
                  <p style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>{purchasingPkg.pkg.description}</p>
                )}
                {purchaseError && <p style={{ color: '#e53935', fontSize: 13, marginBottom: 12 }}>{purchaseError}</p>}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="upcoming-btn upcoming-btn-cancel"
                    style={{ flex: 1, padding: '14px 0' }}
                    onClick={() => { setPurchasingPkg(null); setPurchaseError(''); }}
                  >
                    CANCEL
                  </button>
                  <button
                    className="auth-button"
                    style={{ flex: 1, margin: 0, opacity: purchaseLoading ? 0.6 : 1 }}
                    onClick={handlePackagePurchase}
                    disabled={purchaseLoading}
                  >
                    {purchaseLoading ? 'PROCESSING...' : 'CONFIRM PURCHASE'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Past Appointments */}
          {accountTab === 'history' && (
            <div className="account-section">
              {(accountData.history || []).length > 0 ? (
                (accountData.history || []).map((appt, i) => (
                  <div key={i} className="history-card">
                    <div className="history-card-header">
                      <button
                        className="rebook-history-btn"
                        onClick={() => onRebookClick(appt)}
                        title="Quickly rebook this service">
                        REBOOK
                      </button>
                      <div>
                        <span className="history-date">
                          {appt.startTime ? new Date(appt.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        </span>
                        <span className={`history-status ${appt.status === 2 ? 'closed' : appt.status === 1 ? 'confirmed' : ''}`}>
                          {appt.status === 2 ? 'Closed' : appt.status === 1 ? 'Confirmed' : appt.status === 4 ? 'Checked In' : appt.status === 5 ? 'No Show' : `Status ${appt.status}`}
                        </span>
                      </div>
                      {appt.center?.name && <span className="history-center">{appt.center.name}</span>}
                    </div>
                    <div className="history-card-body">
                      {(appt.services || [{ name: appt.service?.name, therapistName: appt.therapist?.name, isAddon: false, startTime: appt.startTime, endTime: appt.endTime }]).map((svc, j) => (
                        <div key={j} className={`history-service ${svc.isAddon ? 'addon' : ''}`}>
                          <div className="history-service-info">
                            <strong>{svc.isAddon ? '+ ' : ''}{svc.name}</strong>
                            {svc.therapistName && <span className="history-therapist">with {svc.therapistName}</span>}
                            {svc.startTime && (
                              <span className="history-time">
                                {new Date(svc.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                {svc.endTime ? ` - ${new Date(svc.endTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="history-card-footer">
                      <span className="history-price">${appt.price}</span>
                      <div className="history-footer-actions">
                        {!appt.isFeedbackSubmitted && feedbackSuccess !== (appt.id || appt.appointmentId) && appt.startTime && (Date.now() - new Date(appt.startTime).getTime() < 60 * 24 * 60 * 60 * 1000) && (
                          <button className="history-feedback-btn" onClick={() => { setFeedbackAppt(appt); setFeedbackRating(0); setFeedbackComment(''); }}>
                            Leave Feedback
                          </button>
                        )}
                        {(appt.isFeedbackSubmitted || feedbackSuccess === (appt.id || appt.appointmentId)) && (
                          <span className="history-feedback-done">Feedback Submitted</span>
                        )}
                        <button className="history-rebook-btn" onClick={() => onRebookClick(appt)}>
                          Rebook
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="account-empty"><p>No appointment history found</p></div>
              )}
            </div>
          )}

          {/* Gift Cards */}
          {accountTab === 'giftCards' && (
            <div className="account-section">
              {accountData.giftCards?.length > 0 ? (
                accountData.giftCards.map((gc, i) => {
                  const isExpanded = expandedGc === (gc.id || i);
                  const neverExpires = !gc.expiryDate || new Date(gc.expiryDate).getFullYear() > 2100;
                  return (
                    <div key={gc.id || i} className="account-card gc-card" onClick={() => setExpandedGc(isExpanded ? null : (gc.id || i))} style={{ cursor: 'pointer' }}>
                      <div className="account-card-header">
                        <strong>{gc.cardNumber || 'Gift Card'}</strong>
                        <span className="account-card-price">${gc.balance?.toFixed(2)}</span>
                      </div>
                      <div className="gc-summary">
                        <span>Balance: <strong>${gc.balance?.toFixed(2)}</strong></span>
                        <span>{neverExpires ? 'Never Expires' : `Expires: ${new Date(gc.expiryDate).toLocaleDateString()}`}</span>
                        <span>{gc.type || 'Amount'}</span>
                      </div>
                      {isExpanded && (
                        <div className="gc-details">
                          <div className="gc-details-grid">
                            {gc.originalValue > 0 && (
                              <div className="gc-detail-item">
                                <span className="gc-detail-label">Value</span>
                                <span className="gc-detail-value">${gc.originalValue.toFixed(2)}</span>
                              </div>
                            )}
                            {gc.occasion && (
                              <div className="gc-detail-item">
                                <span className="gc-detail-label">Occasion</span>
                                <span className="gc-detail-value">{gc.occasion}</span>
                              </div>
                            )}
                            <div className="gc-detail-item">
                              <span className="gc-detail-label">Single use</span>
                              <span className="gc-detail-value">{gc.isOneTimeUse ? 'Yes' : 'No'}</span>
                            </div>
                            {gc.saleDate && (
                              <div className="gc-detail-item">
                                <span className="gc-detail-label">Activation date</span>
                                <span className="gc-detail-value">{new Date(gc.saleDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                              </div>
                            )}
                            {gc.giftCardType && (
                              <div className="gc-detail-item">
                                <span className="gc-detail-label">Gift card type</span>
                                <span className="gc-detail-value">{gc.giftCardType}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="gc-expand-hint">{isExpanded ? 'Tap to collapse' : 'Tap for details'}</div>
                    </div>
                  );
                })
              ) : (
                <div className="account-empty"><p>No gift cards on file</p></div>
              )}
            </div>
          )}

          {/* Packages */}
          {accountTab === 'packages' && (
            <div className="account-section">
              {accountData.packages?.length > 0 ? (
                accountData.packages.map((pkg, i) => {
                  const isExpanded = expandedPkg === (pkg.id || i);
                  const used = (pkg.totalCount || 0) - (pkg.remainingCount || 0);
                  const pct = pkg.totalCount > 0 ? Math.round((used / pkg.totalCount) * 100) : 0;
                  return (
                    <div key={pkg.id || i} className={`account-card expandable ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => setExpandedPkg(isExpanded ? null : (pkg.id || i))}>
                      <div className="account-card-header">
                        <strong>{pkg.name}</strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className={`account-card-badge ${pkg.statusLabel === 'Active' ? 'active' : ''}`}>{pkg.statusLabel || pkg.status}</span>
                          <span className={`pkg-expand-chevron ${isExpanded ? 'open' : ''}`}>&#8250;</span>
                        </div>
                      </div>
                      <div className="account-card-details">
                        {pkg.remainingCount != null && pkg.totalCount != null && <span>{pkg.remainingCount} of {pkg.totalCount} remaining</span>}
                        {pkg.invoiceNo && <span>Receipt: {pkg.invoiceNo}</span>}
                        {pkg.expiryDate && <span>Expires: {new Date(pkg.expiryDate).toLocaleDateString()}</span>}
                      </div>

                      {/* Progress bar */}
                      {pkg.totalCount > 0 && (
                        <div className="pkg-progress-bar">
                          <div className="pkg-progress-fill" style={{ width: `${pct}%` }} />
                        </div>
                      )}

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="account-card-expand" onClick={e => e.stopPropagation()}>
                          {pkg.services && pkg.services.length > 0 ? (
                            <div className="pkg-services-list">
                              <div className="pkg-services-title">INCLUDED SERVICES</div>
                              {pkg.services.map((svc, j) => (
                                <div key={j} className="pkg-service-row">
                                  <span className="pkg-service-name">{svc.name || svc.service_name || 'Service'}</span>
                                  {(svc.remaining != null || svc.total != null) && (
                                    <span className="pkg-service-count">
                                      {svc.remaining ?? '?'} of {svc.total ?? '?'}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="pkg-no-services">
                              {used > 0
                                ? `${used} session${used > 1 ? 's' : ''} used, ${pkg.remainingCount} remaining`
                                : 'No session details available'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="account-empty"><p>No packages on file</p></div>
              )}
            </div>
          )}

          {/* Forms */}
          {accountTab === 'forms' && (
            activeForm ? (
              <FormRenderer
                formType={activeForm.customFormType}
                guestInfo={guestInfo}
                formMeta={activeForm}
                onComplete={() => {
                  setActiveForm(null);
                  setAccountForms(null); // Force reload
                  window.scrollTo(0, 0);
                }}
                onBack={() => {
                  setActiveForm(null);
                  setAccountForms(null);
                  window.scrollTo(0, 0);
                }}
              />
            ) : (
              <div className="account-section">
                {formsLoading ? (
                  <div className="loading-spinner" style={{ margin: '40px auto' }}></div>
                ) : accountForms && accountForms.length > 0 ? (
                  <>
                    <p style={{ color: '#888', fontSize: 13, marginBottom: 16, textAlign: 'center' }}>
                      Please complete these forms before your appointment.
                    </p>
                    {accountForms.map((form, i) => {
                      const customType = matchFormType(form.name);
                      return (
                        <div
                          key={i}
                          className="account-card expandable"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (customType) {
                              setActiveForm({ ...form, customFormType: customType });
                              window.scrollTo(0, 0);
                            } else {
                              window.open(form.formUrl, '_blank');
                            }
                          }}
                        >
                          <div className="account-card-header">
                            <strong>{form.name}</strong>
                            <span className={`account-card-badge ${form.filled ? 'active' : ''}`}>
                              {form.filled ? 'Completed' : 'Incomplete'}
                            </span>
                          </div>
                          <div className="account-card-details">
                            {form.serviceName && <span>{form.serviceName}</span>}
                            <span>{form.formType === 'service' ? 'Service Form' : 'Guest Form'}</span>
                            <span style={{ color: '#F78DA7', fontWeight: 600 }}>
                              {customType ? 'Fill out \u2192' : 'Open \u2192'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="account-empty"><p>No forms to fill out right now</p></div>
                )}
              </div>
            )
          )}

          {/* Profile */}
          {accountTab === 'profile' && (
            <div className="account-section">
              {profileLoading ? (
                <div className="loading-spinner" style={{margin:'40px auto'}}></div>
              ) : profile ? (
                <div className="profile-form">
                  <div className="auth-row">
                    <div className="auth-field">
                      <label className="auth-label">FIRST NAME *</label>
                      <input type="text" className="auth-input" value={profile.firstName}
                        onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))} />
                    </div>
                    <div className="auth-field">
                      <label className="auth-label">LAST NAME *</label>
                      <input type="text" className="auth-input" value={profile.lastName}
                        onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))} />
                    </div>
                  </div>

                  <div className="auth-field">
                    <label className="auth-label" style={{color:'#999'}}>EMAIL</label>
                    <input type="email" className="auth-input" value={profile.email} readOnly
                      style={{background:'#f5f5f5', color:'#999', cursor:'not-allowed'}} />
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">PHONE</label>
                    <input type="text" className="auth-input" value={profile.phone}
                      onChange={e => setProfile(p => ({ ...p, phone: formatPhoneInput(e.target.value) }))} />
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">DATE OF BIRTH</label>
                    <input type="date" className="auth-input" value={profile.dob}
                      onChange={e => setProfile(p => ({ ...p, dob: e.target.value }))} />
                  </div>

                  <div className="auth-marketing">
                    <p className="auth-marketing-title">Yes please - sign me up to exclusive offers, birthday treats and collaboration freebies!</p>
                    <label className="auth-checkbox-label">
                      <input type="checkbox" checked={profile.marketingEmail}
                        onChange={e => setProfile(p => ({ ...p, marketingEmail: e.target.checked }))} />
                      Email
                    </label>
                    <label className="auth-checkbox-label">
                      <input type="checkbox" checked={profile.marketingSms}
                        onChange={e => setProfile(p => ({ ...p, marketingSms: e.target.checked }))} />
                      SMS
                    </label>
                  </div>

                  {profileError && <p className="auth-error">{profileError}</p>}
                  {profileSuccess && <p className="profile-success">{profileSuccess}</p>}

                  <button className="auth-button" onClick={handleProfileUpdate} disabled={profileSaving}>
                    {profileSaving ? 'UPDATING...' : 'UPDATE'}
                  </button>
                </div>
              ) : (
                <div className="account-empty"><p>Could not load profile</p></div>
              )}
            </div>
          )}
        </div>
      )}

      <button className="auth-link account-desktop-title" onClick={onBackToBooking} style={{ marginTop: '30px' }}>
        ← Back to Booking
      </button>
    </div>
  );
}

export default AccountPage;
